import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, X, FileText, Bot, Cpu, Zap, ChevronRight } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function XORAAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Xin chào! Tôi là Trợ lý ảo XORA từ X-OR CLOUD. Tôi có thể giúp gì cho bạn trong việc lập báo cáo xDataAudit hôm nay?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMsg: Message = { role: 'user', content: inputValue };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    // Simulate XORA (LLM) Response
    setTimeout(() => {
      let response = '';
      const text = userMsg.content.toLowerCase();
      
      if (text.includes('luật') || text.includes('văn bản')) {
        response = 'Dựa trên cơ sở dữ liệu pháp luật 2024-2025, bạn có thể tham khảo Luật Dữ liệu 2024 và Nghị định 165 CP vừa được ban hành. Bạn cần thông tin cụ thể về điều khoản nào?';
      } else if (text.includes('kpi') || text.includes('phiếu') || text.includes('báo cáo') || text.includes('dl01')) {
        response = 'Để tự khai báo KPI, bạn truy cập mục "Tạo Phiếu KPI mới" ở menu bên trái. Phiếu gồm 8 tiêu chí / 27 KPI theo Phụ lục II NĐ 278/2025. Tôi có thể hướng dẫn bạn từng bước điền KPI và upload bằng chứng nếu cần.';
      } else {
        response = 'Tôi đã ghi nhận yêu cầu của bạn. Với tư cách là Trợ lý XORA, tôi đang phân tích dữ liệu từ X-OR CLOUD để đưa ra phản hồi chính xác nhất cho nghiệp vụ kiểm toán của bạn.';
      }

      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      setIsTyping(false);
    }, 1500);
  };

  return (
    <>
      {/* Floating Trigger Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-8 right-8 w-16 h-16 rounded-[2rem] bg-gradient-to-br from-indigo-600 via-indigo-700 to-cyan-600 text-white shadow-[0_20px_50px_rgba(79,70,229,0.4)] z-[9999] flex items-center justify-center border border-white/20 group"
      >
        <div className="absolute inset-0 bg-white/20 rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity blur-md" />
        <AnimatePresence mode="wait">
          {!isOpen ? (
            <motion.div
              key="icon"
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 45 }}
            >
              <Sparkles className="w-8 h-8" />
            </motion.div>
          ) : (
            <X className="w-6 h-6" />
          )}
        </AnimatePresence>
        
        {/* Pulsing Ring */}
        <span className="absolute inset-0 rounded-[2rem] border-2 border-indigo-500 animate-ping opacity-20" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8, filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 100, scale: 0.8, filter: 'blur(10px)' }}
            className="fixed bottom-28 right-8 w-[420px] h-[600px] bg-white dark:bg-[#0F0F12] border border-slate-200 dark:border-white/10 rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.25)] z-[9999] flex flex-col overflow-hidden backdrop-blur-3xl"
          >
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 blur-3xl rounded-full" />
                <div className="flex items-center gap-4 relative z-10">
                   <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                      <Bot className="w-7 h-7 text-cyan-300" />
                   </div>
                   <div>
                      <h4 className="font-heading font-black text-lg tracking-tight flex items-center gap-2">
                        XORA <span className="text-[10px] bg-cyan-400/20 text-cyan-200 px-2 py-0.5 rounded-full border border-cyan-400/30 uppercase tracking-widest font-black">AI Assistant</span>
                      </h4>
                      <div className="flex items-center gap-2 mt-0.5">
                         <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(52,211,153,0.8)]" />
                         <p className="text-[10px] font-black text-indigo-100 uppercase tracking-widest opacity-80">Connected to X-OR CLOUD LLM</p>
                      </div>
                   </div>
                   <button 
                    onClick={() => setIsOpen(false)}
                    className="ml-auto w-10 h-10 rounded-xl hover:bg-white/10 flex items-center justify-center transition-all"
                   >
                      <X size={20} />
                   </button>
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar scroll-smooth" ref={scrollRef}>
                <div className="flex items-center gap-3 justify-center mb-4">
                   <div className="h-px bg-slate-100 dark:bg-white/5 flex-1" />
                   <span className="text-[9px] font-black text-slate-400 uppercase tracking-[.2em]">Hôm nay</span>
                   <div className="h-px bg-slate-100 dark:bg-white/5 flex-1" />
                </div>

                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: msg.role === 'user' ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] p-4 rounded-3xl text-[13px] font-bold leading-relaxed shadow-sm ${
                      msg.role === 'user' 
                        ? 'bg-indigo-600 text-white rounded-tr-none shadow-indigo-500/10' 
                        : 'bg-slate-50 dark:bg-white/5 text-slate-900 dark:text-slate-100 rounded-tl-none border border-slate-100 dark:border-white/10'
                    }`}>
                      {msg.content}
                    </div>
                  </motion.div>
                ))}

                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-3xl rounded-tl-none border border-slate-100 dark:border-white/10">
                       <div className="flex gap-1.5">
                          <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
                          <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
                          <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" />
                       </div>
                    </div>
                  </div>
                )}
            </div>

            {/* Quick Actions */}
            <div className="px-6 py-3 flex gap-2 overflow-x-auto no-scrollbar border-t border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-black/20">
               {[
                 { icon: FileText, label: 'Phiếu KPI' },
                 { icon: Zap, label: 'Thẩm định nộp' },
                 { icon: Cpu, label: 'SOC Connect' }
               ].map((act, i) => (
                 <button key={i} className="whitespace-nowrap flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-[10px] font-black text-slate-600 dark:text-slate-400 hover:border-indigo-500 transition-all shadow-sm">
                    <act.icon size={12} className="text-indigo-500" /> {act.label}
                 </button>
               ))}
            </div>

            {/* Input Area */}
            <div className="p-6 pt-2">
                <div className="relative group">
                   <input 
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                      placeholder="Nhập câu hỏi cho XORA..."
                      className="w-full pl-6 pr-16 py-4 bg-slate-100 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-[1.7rem] outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm font-bold text-slate-900 dark:text-white shadow-inner"
                   />
                   <button 
                    onClick={handleSend}
                    disabled={!inputValue.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center hover:bg-indigo-500 disabled:opacity-30 disabled:hover:bg-indigo-600 transition-all shadow-lg shadow-indigo-600/20 active:scale-95"
                   >
                      <Send size={18} />
                   </button>
                </div>
                <div className="mt-4 flex items-center justify-between px-2">
                   <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Enterprise AI Security Layer v3.2</p>
                   <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-black text-indigo-500">POWERED BY X-OR CLOUD</span>
                      <ChevronRight size={10} className="text-indigo-500" />
                   </div>
                </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
